import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api','') || '';

export default function MemberNutrition() {
  const [date, setDate]     = useState(new Date().toISOString().split('T')[0]);
  const [data, setData]     = useState({ logs:[], totals:{}, date });
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm]     = useState({ meal_type:'breakfast', meal_name:'', calories:'', protein:'', carbs:'', fats:'', notes:'' });
  const [photo, setPhoto]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const load = useCallback(() => {
    axios.get('/member/nutrition', { params:{ date } }).then(r => setData(r.data));
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async e => {
    e.preventDefault(); setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => fd.append(k, v));
      fd.append('date', date);
      if (photo) fd.append('photo', photo);
      await axios.post('/member/nutrition', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
      setMsg('Meal logged!'); setForm({ meal_type:'breakfast', meal_name:'', calories:'', protein:'', carbs:'', fats:'', notes:'' }); setPhoto(null); setShowAdd(false); load();
    } catch { setMsg('Error saving.'); } finally { setSaving(false); }
  };

  const remove = async id => { await axios.delete(`/member/nutrition/${id}`); load(); };

  const t = data.totals || {};
  const CAL_GOAL = 2400, PROTEIN_GOAL = 180, CARBS_GOAL = 300, FATS_GOAL = 80;

  const MacroBar = ({ label, val, goal, color }) => (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:12 }}>
        <span style={{ color:'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontWeight:600 }}>{Math.round(val||0)} / {goal}g</span>
      </div>
      <div style={{ height:5, background:'var(--surface-2)', borderRadius:3, overflow:'hidden' }}>
        <div style={{ height:'100%', borderRadius:3, background:color, width:`${Math.min(100, ((val||0)/goal)*100)}%`, transition:'.3s' }} />
      </div>
    </div>
  );

  const mealIcon = { breakfast:'🥣', lunch:'🥗', dinner:'🍽️', snack:'🍌' };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Nutrition</h1><p className="page-sub">Track meals and macros</p></div>
        <div style={{ display:'flex', gap:8 }}>
          <input type="date" className="input" style={{ width:170 }} value={date} onChange={e => setDate(e.target.value)} />
          <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Log meal</button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:18 }}>
        {/* Summary */}
        <div className="card" style={{ padding:18 }}>
          <div style={{ textAlign:'center', marginBottom:18 }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:32, fontWeight:700 }}>{Math.round(t.cal||0)}</div>
            <div style={{ fontSize:12, color:'var(--text-muted)' }}>of {CAL_GOAL} kcal goal</div>
            <div style={{ width:'100%', height:6, background:'var(--surface-2)', borderRadius:3, marginTop:10, overflow:'hidden' }}>
              <div style={{ height:'100%', borderRadius:3, background:'var(--accent)', width:`${Math.min(100,((t.cal||0)/CAL_GOAL)*100)}%` }} />
            </div>
          </div>
          <MacroBar label="Protein" val={t.protein} goal={PROTEIN_GOAL} color="#3b82f6" />
          <MacroBar label="Carbs"   val={t.carbs}   goal={CARBS_GOAL}   color="#f97316" />
          <MacroBar label="Fats"    val={t.fats}    goal={FATS_GOAL}    color="#10b981" />
          <div style={{ marginTop:12, padding:'10px 14px', background:'var(--surface-2)', borderRadius:'var(--radius)', fontSize:12, color:'var(--text-secondary)' }}>
            Remaining: <strong style={{ color:'var(--text-primary)' }}>{Math.max(0, CAL_GOAL - Math.round(t.cal||0))} kcal</strong>
          </div>
        </div>

        {/* Meals list */}
        <div className="card">
          <div className="card-header"><div className="card-title">🍽️ Meals</div></div>
          {data.logs.length === 0 ? (
            <div className="empty-state" style={{ padding:32 }}><div className="icon">🥗</div><p>No meals logged yet.<br/>Tap + Log meal to start.</p></div>
          ) : (
            <div>
              {data.logs.map(log => (
                <div key={log.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 18px', borderBottom:'1px solid var(--border)' }}>
                  {log.photo ? (
                    <img src={`${API_URL}${log.photo}`} alt="" style={{ width:44, height:44, borderRadius:'var(--radius)', objectFit:'cover', flexShrink:0 }} />
                  ) : (
                    <div style={{ width:44, height:44, borderRadius:'var(--radius)', background:'var(--surface-2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{mealIcon[log.meal_type]||'🍽️'}</div>
                  )}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500 }}>{log.meal_name}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', textTransform:'capitalize' }}>{log.meal_type} · P:{log.protein}g C:{log.carbs}g F:{log.fats}g</div>
                  </div>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', whiteSpace:'nowrap' }}>{log.calories} kcal</div>
                  <button onClick={() => remove(log.id)} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:16, cursor:'pointer', padding:4 }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add meal modal */}
      {showAdd && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div className="card" style={{ width:'100%', maxWidth:480, maxHeight:'90vh', overflow:'auto' }}>
            <div className="card-header">
              <div className="card-title">🍽️ Log a meal</div>
              <button onClick={() => setShowAdd(false)} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:18 }}>✕</button>
            </div>
            <form onSubmit={handleAdd} style={{ padding:20 }}>
              {msg && <div style={{ marginBottom:12, padding:'9px 14px', borderRadius:'var(--radius)', background:'var(--success-dim)', color:'var(--success)', fontSize:13 }}>{msg}</div>}
              <div className="form-row">
                <div className="form-group"><label className="label">Meal type</label>
                  <select className="select" value={form.meal_type} onChange={e => setForm(p=>({...p,meal_type:e.target.value}))}>
                    <option value="breakfast">Breakfast</option><option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option><option value="snack">Snack</option>
                  </select>
                </div>
                <div className="form-group"><label className="label">Meal name *</label><input className="input" placeholder="e.g. Chicken biryani" value={form.meal_name} onChange={e => setForm(p=>({...p,meal_name:e.target.value}))} required /></div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10 }}>
                {[['calories','Calories','kcal'],['protein','Protein','g'],['carbs','Carbs','g'],['fats','Fats','g']].map(([k,l,u]) => (
                  <div key={k} className="form-group">
                    <label className="label">{l} ({u})</label>
                    <input className="input" type="number" placeholder="0" value={form[k]} onChange={e => setForm(p=>({...p,[k]:e.target.value}))} />
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label className="label">Photo (optional)</label>
                <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files[0])} style={{ color:'var(--text-secondary)', fontSize:13 }} />
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button className="btn-primary" type="submit" disabled={saving} style={{ flex:1, justifyContent:'center' }}>{saving ? 'Saving…' : 'Log meal'}</button>
                <button type="button" className="btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
